<template>
  <div v-if="enabled" class="custom-chat-widget" :class="{ 'is-fullscreen': isFullscreen, 'is-open': isOpen }">
    <!-- Chat Toggle Button (Window Mode Only) -->
    <button
      v-if="!isFullscreen"
      class="chat-toggle"
      :class="{ 'is-open': isOpen }"
      @click="toggleChat"
      :aria-label="isOpen ? 'Close chat' : 'Open chat'"
      :style="toggleButtonStyle"
    >
      <svg v-if="!isOpen" class="icon-chat" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
      <svg v-else class="icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
      <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
    </button>

    <!-- Chat Container -->
    <div v-show="isOpen || isFullscreen" class="chat-container" :style="containerStyle">
      <!-- Header -->
      <div class="chat-header" :style="headerStyle">
        <div class="header-brand">
          <svg class="header-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <span class="header-title">Chat Support</span>
        </div>
        <div class="header-actions">
          <button 
            v-if="messages.length > 0" 
            @click="clearChat" 
            class="action-btn" 
            title="Start new conversation"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button 
            v-if="!isFullscreen" 
            @click="minimizeChat" 
            class="action-btn" 
            title="Minimize"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button 
            @click="closeChat" 
            class="action-btn close-btn" 
            title="Close chat"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Messages Area -->
      <div ref="messagesContainer" class="messages-area" :style="messagesAreaStyle">
        <!-- Welcome Screen -->
        <div v-if="showWelcomeScreen && messages.length === 0" class="welcome-screen">
          <div class="welcome-icon">💬</div>
          <h4 class="welcome-title">{{ config.messages?.title || 'Welcome!' }}</h4>
          <p class="welcome-subtitle">{{ config.messages?.subtitle || 'How can we help you today?' }}</p>
          <div v-if="config.initialMessages?.length" class="quick-replies">
            <button
              v-for="(msg, idx) in config.initialMessages"
              :key="idx"
              @click="sendMessage(msg)"
              class="quick-reply-btn"
            >
              <span class="quick-reply-text">{{ msg }}</span>
              <svg class="quick-reply-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div v-for="(message, index) in messages" :key="index" class="message-wrapper" :class="`message-${message.role}`">
          <div class="message-bubble" :style="getMessageStyle(message.role)">
            <div class="message-content" v-html="formatMessage(message.content)"></div>
            <div class="message-time">{{ formatTime(message.timestamp) }}</div>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-if="isTyping" class="message-wrapper message-assistant">
          <div class="message-bubble typing-indicator" :style="getMessageStyle('assistant')">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{{ error }}</span>
        </div>
      </div>

      <!-- Input Area -->
      <div class="input-area" :style="inputAreaStyle">
        <!-- File Upload Preview -->
        <div v-if="uploadedFile" class="file-preview">
          <div class="file-info">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{{ uploadedFile.name }}</span>
          </div>
          <button @click="removeFile" class="remove-file-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="input-controls">
          <!-- File Upload Button -->
          <label v-if="config.allowFileUploads" class="file-upload-btn" :style="{ color: config.customColors?.primary }">
            <input
              type="file"
              ref="fileInput"
              @change="handleFileUpload"
              :accept="config.allowedFilesMimeTypes || '*'"
              style="display: none"
            />
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </label>

          <!-- Text Input -->
          <textarea
            ref="messageInput"
            v-model="currentMessage"
            @keydown.enter.exact.prevent="handleSend"
            @keydown.meta.enter="insertNewline"
            @keydown.ctrl.enter="insertNewline"
            @input="autoResize"
            :placeholder="config.messages?.input_placeholder || 'Type your message...'"
            :disabled="isTyping"
            class="message-input"
            rows="1"
          ></textarea>

          <!-- Send Button -->
          <button
            @click="handleSend"
            :disabled="!canSend"
            class="send-btn"
            :style="sendButtonStyle"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>

        <!-- Footer -->
        <div v-if="config.messages?.footer" class="chat-footer">
          {{ config.messages.footer }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { Modal } from 'ant-design-vue'
import type { N8nChatConfig } from '@/shared/types/n8n'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

const props = defineProps<{
  config: N8nChatConfig
}>()

const enabled = ref(true)
const isOpen = ref(false)
const isTyping = ref(false)
const messages = ref<ChatMessage[]>([])
const currentMessage = ref('')
const messagesContainer = ref<HTMLElement | null>(null)
const messageInput = ref<HTMLTextAreaElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const uploadedFile = ref<File | null>(null)
const sessionId = ref<string>('')
const unreadCount = ref(0)
const error = ref<string | null>(null)
const autoMessageTimer = ref<number | null>(null)
const hasUserEngaged = ref(false)

const isFullscreen = computed(() => props.config.mode === 'fullscreen')
const showWelcomeScreen = computed(() => props.config.showWelcomeScreen && messages.value.length === 0)
const canSend = computed(() => (currentMessage.value.trim().length > 0 || uploadedFile.value) && !isTyping.value)

// Styles
const toggleButtonStyle = computed(() => ({
  backgroundColor: props.config.customColors?.primary || '#e74266',
  color: '#ffffff',
}))

const headerStyle = computed(() => ({
  backgroundColor: props.config.customColors?.primary || '#e74266',
  color: '#ffffff',
}))

const containerStyle = computed(() => {
  if (isFullscreen.value) {
    return {
      width: '100%',
      height: '100vh',
      maxWidth: '100%',
      maxHeight: '100%',
    }
  }
  return {
    width: props.config.dimensions?.width || '400px',
    height: props.config.dimensions?.height || '600px',
  }
})

const messagesAreaStyle = computed(() => ({
  backgroundColor: props.config.customColors?.background || '#ffffff',
}))

const inputAreaStyle = computed(() => ({
  backgroundColor: props.config.customColors?.background || '#ffffff',
  borderTopColor: props.config.customColors?.light || '#e2e8f0',
}))

const sendButtonStyle = computed(() => ({
  backgroundColor: props.config.customColors?.primary || '#e74266',
  color: '#ffffff',
}))

function getMessageStyle(role: string) {
  if (role === 'user') {
    return {
      backgroundColor: props.config.customColors?.primary || '#e74266',
      color: '#ffffff',
    }
  }
  return {
    backgroundColor: props.config.customColors?.light || '#f2f4f8',
    color: props.config.customColors?.text || '#101330',
  }
}

function toggleChat() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    unreadCount.value = 0
    nextTick(() => {
      messageInput.value?.focus()
      scrollToBottom()
    })
  }
}

function minimizeChat() {
  isOpen.value = false
}

function closeChat() {
  isOpen.value = false
}

function clearChat() {
  Modal.confirm({
    title: 'Start New Conversation',
    content: 'This will clear the current chat. Are you sure?',
    okText: 'Yes, Start New',
    okType: 'primary',
    cancelText: 'Cancel',
    onOk() {
      messages.value = []
      sessionId.value = generateSessionId()
      saveSession()
    },
  })
}

async function handleSend() {
  if (!canSend.value) return

  const messageText = currentMessage.value.trim()
  const file = uploadedFile.value

  if (!messageText && !file) return

  // Mark user as engaged and clear auto-message timer
  hasUserEngaged.value = true
  clearAutoMessageTimer()

  // Add user message
  const userMessage: ChatMessage = {
    role: 'user',
    content: messageText,
    timestamp: new Date(),
  }
  messages.value.push(userMessage)

  // Clear input
  currentMessage.value = ''
  uploadedFile.value = null
  error.value = null

  // Auto-resize textarea
  if (messageInput.value) {
    messageInput.value.style.height = 'auto'
  }

  scrollToBottom()

  // Send to n8n webhook
  isTyping.value = true

  try {
    const formData = new FormData()
    formData.append('action', 'sendMessage')
    formData.append('sessionId', sessionId.value)
    formData.append(props.config.chatInputKey || 'chatInput', messageText)

    if (file) {
      formData.append('file', file)
    }

    // Add custom headers if configured
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    }

    const response = await fetch(props.config.webhookUrl, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const data = await response.json()

    // Handle streaming if enabled
    if (props.config.enableStreaming && data.streaming) {
      await handleStreamingResponse(data)
    } else {
      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.output || data.response || 'I received your message.',
        timestamp: new Date(),
      }
      messages.value.push(assistantMessage)
    }

    saveSession()
  } catch (err) {
    console.error('Error sending message:', err)
    error.value = err instanceof Error ? err.message : 'Failed to send message. Please try again.'
  } finally {
    isTyping.value = false
    scrollToBottom()
  }
}

async function handleStreamingResponse(data: any) {
  const assistantMessage: ChatMessage = {
    role: 'assistant',
    content: '',
    timestamp: new Date(),
  }
  messages.value.push(assistantMessage)

  // Simulate streaming (actual implementation depends on n8n streaming format)
  const text = data.output || data.response || ''
  const words = text.split(' ')

  for (let i = 0; i < words.length; i++) {
    assistantMessage.content += (i > 0 ? ' ' : '') + words[i]
    await new Promise(resolve => setTimeout(resolve, 50))
    scrollToBottom()
  }
}

function handleFileUpload(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]

  if (file) {
    // Validate file type if specified
    if (props.config.allowedFilesMimeTypes) {
      const allowed = props.config.allowedFilesMimeTypes.split(',').map(m => m.trim())
      if (allowed.length > 0 && !allowed.includes(file.type)) {
        error.value = `File type not allowed. Accepted types: ${props.config.allowedFilesMimeTypes}`
        return
      }
    }

    uploadedFile.value = file
    error.value = null
  }
}

function removeFile() {
  uploadedFile.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function insertNewline() {
  currentMessage.value += '\n'
  autoResize()
}

function autoResize() {
  nextTick(() => {
    if (messageInput.value) {
      messageInput.value.style.height = 'auto'
      messageInput.value.style.height = `${messageInput.value.scrollHeight}px`
    }
  })
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

function sendMessage(text: string) {
  currentMessage.value = text
  handleSend()
}

function formatMessage(content: string): string {
  // Basic markdown-like formatting
  return content
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

function saveSession() {
  if (!props.config.loadPreviousSession) return

  try {
    localStorage.setItem('cnfltd_chat_session', JSON.stringify({
      sessionId: sessionId.value,
      messages: messages.value,
      timestamp: Date.now(),
    }))
  } catch (err) {
    console.warn('Failed to save chat session:', err)
  }
}

function loadSession() {
  if (!props.config.loadPreviousSession) return

  try {
    const saved = localStorage.getItem('cnfltd_chat_session')
    if (saved) {
      const data = JSON.parse(saved)
      
      // Only load if session is less than 24 hours old
      const age = Date.now() - data.timestamp
      if (age < 24 * 60 * 60 * 1000) {
        sessionId.value = data.sessionId
        messages.value = data.messages.map((m: any) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }))
      }
    }
  } catch (err) {
    console.warn('Failed to load chat session:', err)
  }
}

// Initialize
onMounted(() => {
  if (isFullscreen.value) {
    isOpen.value = true
  }

  loadSession()

  if (!sessionId.value) {
    sessionId.value = generateSessionId()
  }

  // Start auto-message timer if enabled
  startAutoMessageTimer()
})

onUnmounted(() => {
  clearAutoMessageTimer()
})

// Start auto-message timer
function startAutoMessageTimer() {
  // Check if auto-message is enabled
  if (!props.config.autoMessageEnabled) {
    return
  }

  // Don't send if user has already engaged
  if (hasUserEngaged.value || messages.value.length > 0) {
    return
  }

  const delay = (props.config.autoMessageDelay || 5) * 1000 // Convert to milliseconds

  autoMessageTimer.value = window.setTimeout(() => {
    sendAutoMessage()
  }, delay)
}

// Clear auto-message timer
function clearAutoMessageTimer() {
  if (autoMessageTimer.value) {
    clearTimeout(autoMessageTimer.value)
    autoMessageTimer.value = null
  }
}

// Send auto-message
function sendAutoMessage() {
  // Don't send if user has already engaged
  if (hasUserEngaged.value || messages.value.length > 0) {
    return
  }

  const autoMessageText = props.config.autoMessageText || 'Hi! 👋 Is there anything I can help you with?'

  // Add as assistant message
  const autoMessage: ChatMessage = {
    role: 'assistant',
    content: autoMessageText,
    timestamp: new Date(),
  }

  messages.value.push(autoMessage)
  saveSession()

  // Set unread count if chat is closed
  if (!isOpen.value && !isFullscreen.value) {
    unreadCount.value = 1
  }
}

// Watch for new messages when chat is closed
watch(messages, (newMessages, oldMessages) => {
  if (!isOpen.value && newMessages.length > oldMessages.length) {
    const lastMessage = newMessages[newMessages.length - 1]
    if (lastMessage.role === 'assistant') {
      unreadCount.value++
    }
  }
})

defineExpose({
  sendMessage,
})
</script>

<style scoped>
.custom-chat-widget {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  position: fixed;
  z-index: 999999;
}

.custom-chat-widget:not(.is-fullscreen) {
  bottom: 20px;
  right: 20px;
}

.custom-chat-widget.is-fullscreen {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
}

/* Toggle Button */
.chat-toggle {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-toggle:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.chat-toggle svg {
  width: 28px;
  height: 28px;
}

.unread-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ff4444;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
  min-width: 18px;
  text-align: center;
}

/* Chat Container */
.chat-container {
  position: absolute;
  bottom: 80px;
  right: 0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
}

.is-fullscreen .chat-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 0;
  box-shadow: none;
}

/* Header */
.chat-header {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.header-icon {
  width: 24px;
  height: 24px;
  color: white;
  flex-shrink: 0;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.action-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  flex-shrink: 0;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.05);
}

.action-btn:active {
  transform: scale(0.95);
}

.action-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

/* Messages Area */
.messages-area {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.messages-area::-webkit-scrollbar {
  width: 6px;
}

.messages-area::-webkit-scrollbar-track {
  background: transparent;
}

.messages-area::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

/* Welcome Screen */
.welcome-screen {
  text-align: center;
  padding: 40px 20px;
  max-width: 320px;
  margin: auto;
}

.welcome-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.welcome-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
}

.welcome-subtitle {
  margin: 0 0 24px;
  color: #718096;
  font-size: 14px;
  line-height: 1.5;
}

.quick-replies {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quick-reply-btn {
  padding: 14px 18px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  text-align: left;
  color: #2d3748;
  font-weight: 500;
}

.quick-reply-btn:hover {
  border-color: currentColor;
  background: #f7fafc;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.quick-reply-text {
  flex: 1;
}

.quick-reply-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  opacity: 0.5;
  transition: all 0.2s;
}

.quick-reply-btn:hover .quick-reply-icon {
  opacity: 1;
  transform: translateX(2px);
}

/* Messages */
.message-wrapper {
  display: flex;
  margin-bottom: 4px;
}

.message-wrapper.message-user {
  justify-content: flex-end;
}

.message-wrapper.message-assistant {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 12px;
  word-wrap: break-word;
}

.message-user .message-bubble {
  border-bottom-right-radius: 4px;
}

.message-assistant .message-bubble {
  border-bottom-left-radius: 4px;
}

.message-content {
  line-height: 1.5;
  font-size: 14px;
}

.message-content :deep(strong) {
  font-weight: 600;
}

.message-content :deep(code) {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
}

.message-time {
  font-size: 11px;
  margin-top: 4px;
  opacity: 0.7;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.6;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c33;
  font-size: 13px;
}

.error-message svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* Input Area */
.input-area {
  border-top: 1px solid;
  padding: 16px;
}

.file-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 12px;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.file-info svg {
  width: 18px;
  height: 18px;
  color: #666;
}

.remove-file-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  color: #999;
}

.remove-file-btn svg {
  width: 16px;
  height: 16px;
}

.input-controls {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.file-upload-btn {
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s;
}

.file-upload-btn:hover {
  background: #f1f5f9;
}

.file-upload-btn svg {
  width: 22px;
  height: 22px;
}

.message-input {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  max-height: 120px;
  overflow-y: auto;
  transition: border-color 0.2s;
}

.message-input:focus {
  outline: none;
  border-color: currentColor;
}

.message-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.send-btn {
  border: none;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.05);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.send-btn svg {
  width: 20px;
  height: 20px;
  transform: rotate(45deg);
}

.chat-footer {
  text-align: center;
  font-size: 11px;
  color: #999;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

/* Responsive */
@media (max-width: 480px) {
  .custom-chat-widget:not(.is-fullscreen) {
    bottom: 10px;
    right: 10px;
    left: 10px;
  }

  .chat-container {
    right: 0;
    left: 0;
    bottom: 70px;
    width: 100% !important;
    max-width: 100% !important;
    border-radius: 12px 12px 0 0;
  }

  .message-bubble {
    max-width: 85%;
  }
}
</style>
